/**
 * External dependencies
 */
import { useCallback, useMemo } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { useStudioContext } from '@src/hooks/studioContext';
import { useUser } from '@src/hooks';
import { AssetTypes } from '@src/hooks/studioContext/CMSParams';
import hasAdminSafeZoneAccess from './hasAdminSafeZoneAccess';
import {
  getSafeZonePresetIdsForAssetTypes,
  getSafeZonePresetOptionsForAssetTypes,
  getSafeZonePresetOptions,
  getSafeZonesForAsset,
  NOT_SET_SAFE_ZONE_PRESET_ID,
} from './safeZoneRegistry';

function useSafeZoneContext() {
  const studioContext = useStudioContext();
  const user = useUser();

  const assetType = studioContext?.assetType;
  const isTemplateAsset = assetType === AssetTypes.Template;
  const contentBankAssetType = studioContext?.contentBankAssetType;
  const cardsAssetType = studioContext?.cardsAssetType;
  const templateAssetTypes = studioContext?.templateAssetTypes;
  const templateAssetType = Array.isArray(templateAssetTypes)
    ? templateAssetTypes[0]
    : null;
  const safeZoneAssetType =
    isTemplateAsset && templateAssetType
      ? templateAssetType
      : assetType;
  const thumbnailChipDirection =
    user?.tenantInfo?.studioSafeZoneThumbnailChipDirection;
  const hasAdminAccess = hasAdminSafeZoneAccess(user);
  const isContentBankAsset = safeZoneAssetType === AssetTypes.ContentBankAsset;
  const isCardsAsset = safeZoneAssetType === AssetTypes.CardsAsset;
  const isAdAsset = safeZoneAssetType === AssetTypes.Ad;
  const safeZoneAssetSubtype = isCardsAsset
    ? cardsAssetType
    : contentBankAssetType;
  const allowPresetOverride = hasAdminAccess || isContentBankAsset;

  const sharedSafeZoneParams = useMemo(
    () => ({
      assetType: safeZoneAssetType,
      contentBankAssetType: safeZoneAssetSubtype,
      thumbnailChipDirection,
    }),
    [safeZoneAssetType, safeZoneAssetSubtype, thumbnailChipDirection]
  );
  const templateSafeZonePresetIds = useMemo(() => {
    if (!isTemplateAsset) {
      return [];
    }

    return getSafeZonePresetIdsForAssetTypes(templateAssetTypes, {
      useDefaultPresets: !hasAdminAccess,
      thumbnailChipDirection,
    });
  }, [
    hasAdminAccess,
    isTemplateAsset,
    templateAssetTypes,
    thumbnailChipDirection,
  ]);

  const shouldAllowPresetOverrideForCurrentAsset = useCallback(
    ({ cmsPageType } = {}) => {
      if (allowPresetOverride) {
        return true;
      }

      if (!isAdAsset) {
        return false;
      }

      return (
        getSafeZonePresetOptions({
          ...sharedSafeZoneParams,
          cmsPageType,
        }).length > 1
      );
    },
    [allowPresetOverride, isAdAsset, sharedSafeZoneParams]
  );

  const resolveSafeZonesForCurrentAsset = useCallback(
    ({
      cmsPageType,
      safeZonePresetId,
      allowPresetOverride: allowPresetOverrideOverride,
    } = {}) => {
      const resolvedAllowPresetOverride =
        isTemplateAsset ||
        (allowPresetOverrideOverride ??
          shouldAllowPresetOverrideForCurrentAsset({ cmsPageType }));
      const templateSafeZonePresetId =
        isTemplateAsset && resolvedAllowPresetOverride
          ? templateSafeZonePresetIds.includes(safeZonePresetId)
            ? safeZonePresetId
            : templateSafeZonePresetIds[0] || NOT_SET_SAFE_ZONE_PRESET_ID
          : safeZonePresetId;

      return getSafeZonesForAsset({
        ...sharedSafeZoneParams,
        cmsPageType,
        safeZonePresetId: templateSafeZonePresetId,
        allowPresetOverride: resolvedAllowPresetOverride,
      });
    },
    [
      isTemplateAsset,
      sharedSafeZoneParams,
      shouldAllowPresetOverrideForCurrentAsset,
      templateSafeZonePresetIds,
    ]
  );

  const getSafeZonePresetOptionsForCurrentAsset = useCallback(
    ({ cmsPageType } = {}) => {
      if (isTemplateAsset) {
        return getSafeZonePresetOptionsForAssetTypes(templateAssetTypes, {
          useDefaultPresets: !hasAdminAccess,
          thumbnailChipDirection,
        });
      }

      return getSafeZonePresetOptions({
        ...sharedSafeZoneParams,
        cmsPageType,
      });
    },
    [
      hasAdminAccess,
      isTemplateAsset,
      sharedSafeZoneParams,
      templateAssetTypes,
      thumbnailChipDirection,
    ]
  );

  return {
    assetType: safeZoneAssetType,
    isTemplateAsset,
    contentBankAssetType,
    cardsAssetType,
    templateAssetTypes,
    thumbnailChipDirection,
    hasAdminAccess,
    isContentBankAsset,
    isCardsAsset,
    isAdAsset,
    safeZoneAssetSubtype,
    allowPresetOverride,
    shouldAllowPresetOverrideForCurrentAsset,
    resolveSafeZonesForCurrentAsset,
    getSafeZonePresetOptionsForCurrentAsset,
  };
}

export default useSafeZoneContext;
